import { useEffect, useMemo, useState } from 'react'
import { defineMessages, useIntl, type IntlShape, type MessageDescriptor } from 'react-intl'

import { useAppSelector } from 'hooks/reduxHooks'
import { slugify } from 'lib/string/stringUtils'
import {
  ARTS_CULTURE_POI_TERMS,
  GOLF_POI_TERMS,
  NIGHTLY_DRINK_INCLUSION_REGEX,
  OFFER_PAGE_HIGHLIGHT_TAG_IDS,
  TRANSPORT_POI_TERMS,
  type OfferPageHighlightTagIcon,
  type OfferPageHighlightTagId,
} from './offerPageHighlightTagConstants'
import {
  getOfferPageHighlightTravellerContext,
  getViewedOfferPageHighlightPropertyIds,
  setViewedOfferPageHighlightPropertyIds,
  shouldUseFirstNameForNeutralOfferPageHighlight,
  type OfferPageHighlightTravellerContext,
} from './offerPageHighlightTagsUtils'

export interface OfferPageHighlightTag {
  id: OfferPageHighlightTagId
  icon: OfferPageHighlightTagIcon
  title: string
  description: string
}

export interface OfferPageHighlightTagContext {
  boardCodes?: Array<App.RateBoardCode>
  charterBundleRoute?: App.CharterBundleRoute
  facilityNames?: Array<string>
  facilities?: Array<string>
  hasAllInclusiveOnAllRates?: boolean
  hasBreakfastIncludedOnAllRates?: boolean
  hasMassageIncludedOnAllRates?: boolean
  hasNightlyDrinkOnAllRates?: boolean
  holidayTypes?: Array<string>
  inclusions?: Array<string>
  inclusionCategories?: Array<App.PackageInclusionCategory>
  isAdultsOnly?: boolean
  locationTags?: Array<string>
  rating?: App.OfferRating
  roomAmenities?: Array<string>
  roomNames?: Array<string>
  roomSizeAvailableRoomCount?: number
  roomSizesSqm?: Array<number>
  roomViewTags?: Array<string>
  surroundings?: App.OfferPropertySurroundings
}

interface Props {
  context?: OfferPageHighlightTagContext
  duration?: number
  hasSelectedDates: boolean
  isOccupancyContextKnown: boolean
  offerPageHighlightTagsOverride?: Array<App.OfferPageHighlightTagOverride>
  propertyId: string
  rooms?: Array<App.Occupants>
}

interface GetTitleOptions {
  duration?: number
  firstName?: string
  hasSelectedDates: boolean
  shouldUseFirstNameForNeutralContext: boolean
  travellerContext: OfferPageHighlightTravellerContext
}

interface OfferPageHighlightTagConfig {
  id: OfferPageHighlightTagId
  icon: OfferPageHighlightTagIcon
  titleMessage: MessageDescriptor
  descriptionMessage: MessageDescriptor
}

interface OfferPageHighlightMappingConfig extends OfferPageHighlightTagConfig {
  isEligible: (options: OfferPageHighlightMappingOptions) => boolean
}

interface OfferPageHighlightMappingOptions {
  context?: OfferPageHighlightTagContext
  hasAllInclusive: boolean
  hasAdultsOnlyText: boolean
  hasAtlasBeachClub: boolean
  hasBreakfastIncluded: boolean
  hasChild: boolean
  hasDailyCocktails: boolean
  hasFamilyFriendlySignals: boolean
  hasMassageIncluded: boolean
  hasNightlyDrink: boolean
  hasRomanticHolidayType: boolean
  hasReturnAirportTransfers: boolean
  hasTotalGuestCountForGroup: boolean
  isAdultsOnlyProperty: boolean
  isStructuredAdultsOnlyProperty: boolean
  isCouple: boolean
  isGroupOrFamily: boolean
  searchableText: Array<string>
}

const titleMessages = defineMessages({
  selectedDatesPersonalised: {
    id: '9qYr362JUbblxreWxURy',
    defaultMessage: '{firstName}, make the most of your {duration}-night escape',
    description: 'Personalised offer page highlight tags heading shown when a logged-in traveller has selected travel dates.',
  },
  selectedDates: {
    id: 'kw_ajbNItS2kRiUYRKj3',
    defaultMessage: 'Make the most of your {duration}-night escape',
    description: 'Offer page highlight tags heading shown when travel dates have been selected.',
  },
  familyPersonalised: {
    id: '1V7zZ4l7GenqmrZUT0qI',
    defaultMessage: '{firstName}, the kids are going to love this place',
    description: 'Personalised offer page highlight tags heading shown for family traveller context.',
  },
  family: {
    id: 'SqrTjifH_VLXlV1_rL4a',
    defaultMessage: 'Great for a family getaway',
    description: 'Offer page highlight tags heading shown for family traveller context without a logged-in traveller name.',
  },
  couplePersonalised: {
    id: 'rP_izX-p5-frQUfaIQgU',
    defaultMessage: '{firstName}, this is perfect for a romantic escape',
    description: 'Personalised offer page highlight tags heading shown for couple traveller context.',
  },
  couple: {
    id: 'KM5TYoW1-Po9X4j9e_UH',
    defaultMessage: 'Perfect for a romantic escape',
    description: 'Offer page highlight tags heading shown for couple traveller context without a logged-in traveller name.',
  },
  group: {
    id: 'BAoWe9-zW73zLiCxKjQ0',
    defaultMessage: 'Great for a getaway with friends',
    description: 'Offer page highlight tags heading shown for adult group traveller context without selected travel dates.',
  },
  neutralPersonalised: {
    id: 'fSY7ROMQAwflYfbpuY34',
    defaultMessage: '{firstName}, we think you’ll love this place',
    description: 'Personalised offer page highlight tags heading shown when no trip or occupancy context is available.',
  },
  neutral: {
    id: 'qFaSn8AG_efcM_5ohbt9',
    defaultMessage: 'Why travellers love this stay',
    description: 'Offer page highlight tags heading shown when no traveller context is available.',
  },
})

const tagMessages = defineMessages({
  maldivesCharterTitle: {
    id: 'BIzJ50sS5u6lZ_m6ZGRs',
    defaultMessage: 'Maldives charter',
    description: 'Title for an offer page highlight tag shown for Maldives charter hotel and flight bundle offers.',
  },
  maldivesCharterDescription: {
    id: 'Gd4eo1OqRFpWebgJoUTC',
    defaultMessage: 'Exclusive for Luxury Escapes guests',
    description: 'Description for an offer page highlight tag shown for Maldives charter hotel and flight bundle offers.',
  },
  familyPoolsTitle: {
    id: 'm6VxiTYi5HF5QkRZmQFh',
    defaultMessage: 'Family pools and activities',
    description: 'Title for a family-focused offer page highlight tag about pools and activities.',
  },
  familyPoolsDescription: {
    id: 'iDTBsx3S5rVNA2APUfPB',
    defaultMessage: 'Kids can splash and stay entertained',
    description: 'Description for a family-focused offer page highlight tag about kids activities.',
  },
  familyLocationTitle: {
    id: 'F3Hd4aE-tXT6-5Phb-Xq',
    defaultMessage: 'Close to beaches and fun',
    description: 'Title for a family-focused offer page highlight tag about nearby attractions.',
  },
  familyLocationDescription: {
    id: 'b2upeAOxCoCsBJXvX16z',
    defaultMessage: 'Easy family day trips nearby',
    description: 'Description for a family-focused offer page highlight tag about nearby attractions.',
  },
  familyDiningTitle: {
    id: '33pYXleyPg6Y_PNMf6WQ',
    defaultMessage: 'Dining for every taste',
    description: 'Title for a family-focused offer page highlight tag about varied dining options.',
  },
  familyDiningDescription: {
    id: '132vpLH-JhFAnxq2IKvQ',
    defaultMessage: 'Options for even picky eaters',
    description: 'Description for a family-focused offer page highlight tag about varied dining options.',
  },
  familySpaceTitle: {
    id: 'mzNjhW4GyJ8Mne7aNoCA',
    defaultMessage: 'Space for families to relax',
    description: 'Title for a family-focused offer page highlight tag about relaxing spaces.',
  },
  familySpaceDescription: {
    id: 'kApUhNaeVqHShqAKdUNy',
    defaultMessage: 'Pools, gardens and open areas',
    description: 'Description for a family-focused offer page highlight tag about relaxing spaces.',
  },
  coupleSunsetTitle: {
    id: 'LFtGgrCyH1NQ4lUx8wwB',
    defaultMessage: 'Sunset views together',
    description: 'Title for a couple-focused offer page highlight tag about watching sunsets together.',
  },
  coupleSunsetDescription: {
    id: 'cCZH6uD0zN0jLK-BTUgY',
    defaultMessage: 'Watch the sun dip below the horizon',
    description: 'Description for a couple-focused offer page highlight tag about watching sunsets together.',
  },
  coupleCocktailsTitle: {
    id: '2LoeAN8M7J7XHK_8i1We',
    defaultMessage: 'Cocktails and relaxed nights',
    description: 'Title for a couple-focused offer page highlight tag about evening drinks.',
  },
  coupleCocktailsDescription: {
    id: 'ibPP0OMZHcpjqkCLs6pX',
    defaultMessage: 'Enjoy drinks and laid-back evenings',
    description: 'Description for a couple-focused offer page highlight tag about evening drinks.',
  },
  coupleResortTitle: {
    id: '55lZReYMAmeWTDK6xeGU',
    defaultMessage: 'Peaceful resort atmosphere',
    description: 'Title for a couple-focused offer page highlight tag about a peaceful resort setting.',
  },
  coupleResortDescription: {
    id: 'vNQ8IX1XZHNRQLvBqYl2',
    defaultMessage: 'Quiet spaces to relax and reconnect',
    description: 'Description for a couple-focused offer page highlight tag about a peaceful resort setting.',
  },
  coupleServiceTitle: {
    id: 'GD71_Ga0zsS7Gtxq_3T7',
    defaultMessage: 'Highly rated for service',
    description: 'Title for a couple-focused offer page highlight tag about highly rated service.',
  },
  coupleServiceDescription: {
    id: '63eRbm0yguf-b7BypOaC',
    defaultMessage: 'Guests praise the warm hospitality',
    description: 'Description for a couple-focused offer page highlight tag about highly rated service.',
  },
  defaultRatingTitle: {
    id: 'Tw1v7IK5vqnbbQRdLlN9',
    defaultMessage: 'Highly rated by travellers',
    description: 'Title for a general offer page highlight tag about traveller ratings.',
  },
  defaultRatingDescription: {
    id: 'tBWq8agANFa171AFelj0',
    defaultMessage: 'Loved for its service and dining',
    description: 'Description for a general offer page highlight tag about traveller ratings.',
  },
  defaultLocationWithDatesTitle: {
    id: 'O4vIjpaVJxx_dNzpCU3w',
    defaultMessage: 'Prime location for exploring',
    description: 'Title for a general offer page highlight tag about location when travel dates are selected.',
  },
  defaultLocationTitle: {
    id: 'g7-YD45lDbU58JBAc_yV',
    defaultMessage: 'Great location to explore',
    description: 'Title for a general offer page highlight tag about location when no travel dates are selected.',
  },
  defaultLocationDescription: {
    id: 'nwFqhp648DX8wmcbqAJ_',
    defaultMessage: 'Close to beaches and restaurants',
    description: 'Description for a general offer page highlight tag about nearby beaches and restaurants.',
  },
  defaultPerksTitle: {
    id: 'zDYLmrrKehpGqGXzye6U',
    defaultMessage: 'Premium perks included',
    description: 'Title for a general offer page highlight tag about included premium perks.',
  },
  defaultPerksDescription: {
    id: 'mG7WQtaDJe3abxEMvvgf',
    defaultMessage: 'Enjoy curated extras and drinks',
    description: 'Description for a general offer page highlight tag about included premium perks.',
  },
  defaultSettingTitle: {
    id: 'hQtgzOA-_jdwz_utpdeS',
    defaultMessage: 'Beautiful resort setting',
    description: 'Title for a general offer page highlight tag about the resort setting.',
  },
  defaultSettingDescription: {
    id: '2Kd8RyU0FSvVFi_Fi7jG',
    defaultMessage: 'Pools, gardens and ocean views',
    description: 'Description for a general offer page highlight tag about the resort setting.',
  },
  greatForCouplesTitle: {
    id: 'OJTntylUVxe3Fc0p968A',
    defaultMessage: 'Great for couples',
    description: 'Title for an offer page highlight tag shown for couple-friendly stays.',
  },
  greatForCouplesDescription: {
    id: 'mRpuGAJvjpzPj_9P_JjJ',
    defaultMessage: 'Made for a romantic escape',
    description: 'Description for an offer page highlight tag shown for couple-friendly stays.',
  },
  familyFriendlyTitle: {
    id: '1P-rGCNE8z5fS4O2zwZ3',
    defaultMessage: 'Family friendly',
    description: 'Title for an offer page highlight tag shown for family-friendly stays.',
  },
  familyFriendlyDescription: {
    id: 'qUEShaMaOW-1VIx_8NQH',
    defaultMessage: 'Designed with families in mind',
    description: 'Description for an offer page highlight tag shown for family-friendly stays.',
  },
  allInclusiveTitle: {
    id: 'ApVNYp_FC6md-YbgNRHz',
    defaultMessage: 'All-inclusive',
    description: 'Title for an offer page highlight tag shown when meals and drinks are included.',
  },
  allInclusiveDescription: {
    id: '0_krx7UFojLlBFxjIS4x',
    defaultMessage: 'Food, drinks and more, all taken care of',
    description: 'Description for an offer page highlight tag shown when meals and drinks are included.',
  },
  breakfastIncludedTitle: {
    id: 'U6211to5p1uDppZatm7U',
    defaultMessage: 'Breakfast included',
    description: 'Title for an offer page highlight tag shown when breakfast is included.',
  },
  breakfastIncludedDescription: {
    id: 'cu0krTfkhyINL07RJeUD',
    defaultMessage: 'Start your day the right way',
    description: 'Description for an offer page highlight tag shown when breakfast is included.',
  },
  massageIncludedTitle: {
    id: 'R41o0gdJ3YNAe9IdsTbI',
    defaultMessage: 'Massage included',
    description: 'Title for an offer page highlight tag shown when a massage or spa treatment is included.',
  },
  massageIncludedDescription: {
    id: '3fKott53-ouPXbsOe5Px',
    defaultMessage: 'A little extra relaxation, included',
    description: 'Description for an offer page highlight tag shown when a massage or spa treatment is included.',
  },
  kidsStayFreeTitle: {
    id: 'mYP5LTjoBkU1CEesZhMf',
    defaultMessage: 'Kids stay free',
    description: 'Title for an offer page highlight tag shown when children can stay free as an inclusion.',
  },
  kidsStayFreeDescription: {
    id: 'e4sLsXDVFennziUagC86',
    defaultMessage: 'Extra value for family stays',
    description: 'Description for an offer page highlight tag shown when children can stay free as an inclusion.',
  },
  diningCreditTitle: {
    id: '_-POXnG8qqjLzsZs2Kla',
    defaultMessage: 'Dining credit',
    description: 'Title for an offer page highlight tag shown when dining credit is included.',
  },
  diningCreditDescription: {
    id: 'tXNfAnKBvrD2gcciBQEm',
    defaultMessage: 'Spend it on delicious moments',
    description: 'Description for an offer page highlight tag shown when dining credit is included.',
  },
  dailyCocktailsTitle: {
    id: '7aGWhediFP9D4GwYyecU',
    defaultMessage: 'Daily cocktails',
    description: 'Title for an offer page highlight tag shown when daily cocktails or nightly drinks are included.',
  },
  dailyCocktailsDescription: {
    id: '2XpS2v7xTL9V4lAAPcvR',
    defaultMessage: 'Raise a glass each day',
    description: 'Description for an offer page highlight tag shown when daily cocktails or nightly drinks are included.',
  },
  nightlyDrinkTitle: {
    id: '-QMUMHrvEJ_JgZCggcHO',
    defaultMessage: 'Nightly drink',
    description: 'Title for an offer page highlight tag shown when a nightly drink is included.',
  },
  nightlyDrinkDescription: {
    id: 'esD-M36yR-vaycSF4Lbz',
    defaultMessage: 'Settle in with a drink each night',
    description: 'Description for an offer page highlight tag shown when a nightly drink is included.',
  },
  lateCheckoutTitle: {
    id: 'fWd01Rpx2C6Qm4bJu57X',
    defaultMessage: 'Late checkout',
    description: 'Title for an offer page highlight tag shown when late checkout is included.',
  },
  lateCheckoutDescription: {
    id: '5U7Qzv2DiZ5xgb5kiZwL',
    defaultMessage: 'Enjoy a little more time before you leave',
    description: 'Description for an offer page highlight tag shown when late checkout is included.',
  },
  highlyRatedTitle: {
    id: 'IUBS6lNraEAJb74OSVng',
    defaultMessage: 'Highly rated by travellers',
    description: 'Title for an offer page highlight tag shown for highly rated properties.',
  },
  highlyRatedDescription: {
    id: '34rfjzNmtWV6llHzY-8z',
    defaultMessage: 'Trusted and loved by guests',
    description: 'Description for an offer page highlight tag shown for highly rated properties.',
  },
  skiInSkiOutTitle: {
    id: '92qta_u97n1tbBj4gTOp',
    defaultMessage: 'Ski-in/ski-out',
    description: 'Title for an offer page highlight tag shown for ski stays.',
  },
  skiInSkiOutDescription: {
    id: 'nu9IlNnrn3Z1WK3IVQWJ',
    defaultMessage: 'Hit the slopes right from your door',
    description: 'Description for an offer page highlight tag shown for ski stays.',
  },
  beachNearbyTitle: {
    id: 'V6ATLyzU-O4zgkkL6ce_',
    defaultMessage: 'Beach nearby',
    description: 'Title for an offer page highlight tag shown for properties near a beach.',
  },
  beachNearbyDescription: {
    id: 'iRnaNTT2DDj47R96X5lH',
    defaultMessage: 'Sand and sea within easy reach',
    description: 'Description for an offer page highlight tag shown for properties near a beach.',
  },
  atlasBeachClubTitle: {
    id: 'grkzj0BK_EbcQZSYvStj',
    defaultMessage: 'Atlas Beach Club',
    description: 'Title for an offer page highlight tag shown when Atlas Beach Club access is included.',
  },
  atlasBeachClubDescription: {
    id: 'H1UNDVgEi1q80jk9F21e',
    defaultMessage: 'Access to the world’s largest beach club',
    description: 'Description for an offer page highlight tag shown when Atlas Beach Club access is included.',
  },
  cannaBaliBeachClubAccessTitle: {
    id: 'qVwqEmhiVfC3gqWLBPxJ',
    defaultMessage: 'CANNA Bali Beach Club Access',
    description: 'Title for an offer page highlight tag shown when CANNA Bali Beach Club access is included.',
  },
  cannaBaliBeachClubAccessDescription: {
    id: 'XPpPaVvNqlL21RoX09e2',
    defaultMessage: 'Bali’s hottest beach club, included',
    description: 'Description for an offer page highlight tag shown when CANNA Bali Beach Club access is included.',
  },
  centralLocationTitle: {
    id: 'o9Le6JPWRylp1nIcs-w5',
    defaultMessage: 'Central location',
    description: 'Title for an offer page highlight tag shown for centrally located properties.',
  },
  centralLocationDescription: {
    id: '3qbH4RLf7Mr1LUB_aWL6',
    defaultMessage: 'Right in the heart of it all',
    description: 'Description for an offer page highlight tag shown for centrally located properties.',
  },
  greatLocationTitle: {
    id: '52uaslsXRuWLnUUJfqcU',
    defaultMessage: 'Great location',
    description: 'Title for an offer page highlight tag shown for properties with a strong location.',
  },
  greatLocationDescription: {
    id: 'uDNzfJKB4Tbzxn1zYvQn',
    defaultMessage: 'Everything on your doorstep',
    description: 'Description for an offer page highlight tag shown for properties with a strong location.',
  },
  walkableNeighbourhoodTitle: {
    id: 'zTqZSSTfyiwXPrWyW-Eg',
    defaultMessage: 'Walkable neighbourhood',
    description: 'Title for an offer page highlight tag shown for walkable neighbourhoods.',
  },
  walkableNeighbourhoodDescription: {
    id: 'Xwarqjf0ZlFG2_fmkGTM',
    defaultMessage: 'Explore the area on foot',
    description: 'Description for an offer page highlight tag shown for walkable neighbourhoods.',
  },
  artsCultureNearbyTitle: {
    id: 'tGIdM59P3LovyOS8RDD-',
    defaultMessage: 'Arts & culture nearby',
    description: 'Title for an offer page highlight tag shown for nearby arts and culture.',
  },
  artsCultureNearbyDescription: {
    id: '_bOHLe5xQKKVx5Q5pmNN',
    defaultMessage: 'Museums, galleries and more on your doorstep',
    description: 'Description for an offer page highlight tag shown for nearby arts and culture.',
  },
  golfNearbyTitle: {
    id: '06djRek7LlcvJzJPNS60',
    defaultMessage: 'Golf nearby',
    description: 'Title for an offer page highlight tag shown for nearby golf.',
  },
  golfNearbyDescription: {
    id: 'Vf0dg4JVs9FFLAguqzI3',
    defaultMessage: 'Tee off without going far',
    description: 'Description for an offer page highlight tag shown for nearby golf.',
  },
  easyAirportAccessTitle: {
    id: 'RpDT-y5t3gLgz9tH3L5R',
    defaultMessage: 'Easy airport access',
    description: 'Title for an offer page highlight tag shown for easy airport access.',
  },
  easyAirportAccessDescription: {
    id: 'ziHVv69SwJzR1hCLNTP9',
    defaultMessage: 'Arrive and depart with ease',
    description: 'Description for an offer page highlight tag shown for easy airport access.',
  },
  closeToTransportTitle: {
    id: 'EFLmszBZ4oYPxNwatW-Z',
    defaultMessage: 'Close to transport',
    description: 'Title for an offer page highlight tag shown for nearby transport.',
  },
  closeToTransportDescription: {
    id: 'ZNbfy4DLjZgp6-Lq3ZKd',
    defaultMessage: 'Easy to get around',
    description: 'Description for an offer page highlight tag shown for nearby transport.',
  },
  oceanViewRoomsTitle: {
    id: 'BWW7AZ3zuxDhPcpWsDYk',
    defaultMessage: 'Ocean-view rooms',
    description: 'Title for an offer page highlight tag shown for ocean-view rooms.',
  },
  oceanViewRoomsDescription: {
    id: 'RMSkKjx_KKfHJGIz7juo',
    defaultMessage: 'Wake up to water views',
    description: 'Description for an offer page highlight tag shown for ocean-view rooms.',
  },
  spaciousRoomsTitle: {
    id: '77_gny1hd5GYzRyKQWWA',
    defaultMessage: 'Spacious rooms',
    description: 'Title for an offer page highlight tag shown for spacious rooms.',
  },
  spaciousRoomsDescription: {
    id: '7ceUIQsKTa0AIe8BNeM4',
    defaultMessage: 'Extra space to relax',
    description: 'Description for an offer page highlight tag shown for spacious rooms.',
  },
  interconnectingRoomsTitle: {
    id: 'wnOkaqmDXlDwUrFgAUMS',
    defaultMessage: 'Interconnecting rooms',
    description: 'Title for an offer page highlight tag shown for interconnecting rooms.',
  },
  interconnectingRoomsDescription: {
    id: 'Z47TOp5ZbpwZJTyiM_KG',
    defaultMessage: 'Perfect for families or groups travelling together',
    description: 'Description for an offer page highlight tag shown for interconnecting rooms.',
  },
  adultsOnlyTitle: {
    id: 'Dh_MPe75w0mgySEM8mLa',
    defaultMessage: 'Adults only',
    description: 'Title for an offer page highlight tag shown for adults-only properties.',
  },
  adultsOnlyDescription: {
    id: 'rSEMpWKnT4tPNgsx71hA',
    defaultMessage: 'A retreat just for grown-ups',
    description: 'Description for an offer page highlight tag shown for adults-only properties.',
  },
  adultsOnlyAreaTitle: {
    id: 'fG4n-fAZV4wHXmrvhVlR',
    defaultMessage: 'Adults-Only Area',
    description: 'Title for an offer page highlight tag shown when a property has areas reserved for adults.',
  },
  adultsOnlyAreaDescription: {
    id: 'KWubSt_vWljQMDgFDjrG',
    defaultMessage: 'Peaceful spaces reserved for adults',
    description: 'Description for an offer page highlight tag shown when a property has areas reserved for adults.',
  },
  freeWifiTitle: {
    id: 'h1G9jdIDaT5d19bkf_Wt',
    defaultMessage: 'Free WiFi',
    description: 'Title for an offer page highlight tag shown when free WiFi is available.',
  },
  freeWifiDescription: {
    id: 'ZsQVrexwSo_D3cmuhu9J',
    defaultMessage: 'Stay connected throughout your stay',
    description: 'Description for an offer page highlight tag shown when free Wi-Fi is available.',
  },
  receptionTitle: {
    id: 'hGV1TnOlP5YdpwNXBA6o',
    defaultMessage: '24-hour reception',
    description: 'Title for an offer page highlight tag shown for 24-hour reception.',
  },
  receptionDescription: {
    id: 'iFsurNDzoNobL17XYuxx',
    defaultMessage: 'Support any time, day or night',
    description: 'Description for an offer page highlight tag shown for 24-hour reception.',
  },
  expressCheckInTitle: {
    id: 'mLAFepNyLN0hjUgpeDf8',
    defaultMessage: 'Express check-in',
    description: 'Title for an offer page highlight tag shown for express check-in.',
  },
  expressCheckInDescription: {
    id: 'G0601bj0YHhKvmbXz2QJ',
    defaultMessage: 'Get settled faster on arrival',
    description: 'Description for an offer page highlight tag shown for express check-in.',
  },
  parkingTitle: {
    id: 'qCc9WR8e6ShTKUhJ56lg',
    defaultMessage: 'Parking',
    description: 'Title for an offer page highlight tag shown when parking is available.',
  },
  parkingDescription: {
    id: 'VjrkZWVEJ9IOsoaAmuNc',
    defaultMessage: 'Make the most of onsite parking',
    description: 'Description for an offer page highlight tag shown when parking is available.',
  },
  airportTransfersTitle: {
    id: 'bMzJonDlJukZZVdGABS7',
    defaultMessage: 'Airport transfers',
    description: 'Title for an offer page highlight tag shown when airport transfers are available.',
  },
  airportTransfersDescription: {
    id: 'agzLMhG-pQR-k8UqLXyO',
    defaultMessage: 'Straight from the airport to your door',
    description: 'Description for an offer page highlight tag shown when airport transfers are available.',
  },
  returnAirportTransfersTitle: {
    id: 'xtjVQZJG2rsAQCrCQ2M3',
    defaultMessage: 'Return airport transfers',
    description: 'Title for an offer page highlight tag shown when return airport transfers are included.',
  },
  returnAirportTransfersDescription: {
    id: '1yqWPA_QBwBW623GiW02',
    defaultMessage: 'Arrive and depart with ease',
    description: 'Description for an offer page highlight tag shown when return airport transfers are included.',
  },
  onsiteDiningTitle: {
    id: 'vh6nA7xiWVLnDCL5Trym',
    defaultMessage: 'Onsite dining',
    description: 'Title for an offer page highlight tag shown when onsite dining is available.',
  },
  onsiteDiningDescription: {
    id: 'LHbXO9G94hVBSmCInaL6',
    defaultMessage: 'Restaurant and dining onsite',
    description: 'Description for an offer page highlight tag shown when onsite dining is available.',
  },
  barLoungeTitle: {
    id: '78xNEp1lxLvo1KLxsppB',
    defaultMessage: 'Bar & lounge onsite',
    description: 'Title for an offer page highlight tag shown when a bar or lounge is available.',
  },
  barLoungeDescription: {
    id: 'pyGovaPyA7qpNxbYDftI',
    defaultMessage: 'Settle in and stay for a drink',
    description: 'Description for an offer page highlight tag shown when a bar or lounge is available.',
  },
  roomServiceTitle: {
    id: 'p97XcSYFzq_PS_7ukNn4',
    defaultMessage: 'Room service',
    description: 'Title for an offer page highlight tag shown when room service is available.',
  },
  roomServiceDescription: {
    id: 'ohY9P4VtSba6-o61UoC2',
    defaultMessage: 'Eat in the comfort of your room',
    description: 'Description for an offer page highlight tag shown when room service is available.',
  },
  indoorPoolTitle: {
    id: '5xRspBR6WzwcXILaXPvu',
    defaultMessage: 'Indoor pool',
    description: 'Title for an offer page highlight tag shown when an indoor pool is available.',
  },
  indoorPoolDescription: {
    id: 'Y1qX2R1Run6grix-wAgp',
    defaultMessage: 'Swim year-round in comfort',
    description: 'Description for an offer page highlight tag shown when an indoor pool is available.',
  },
  kidsPoolTitle: {
    id: '_W3_kpBt48K-RCh86OvK',
    defaultMessage: 'Kids pool',
    description: 'Title for an offer page highlight tag shown when a kids pool is available.',
  },
  kidsPoolDescription: {
    id: 'tOTS8q8GqlPWcHnDrlP_',
    defaultMessage: 'Water fun for kids of all ages',
    description: 'Description for an offer page highlight tag shown when a kids pool is available.',
  },
  fitnessCentreTitle: {
    id: '3-6MO-N6DV558FUQMLwi',
    defaultMessage: 'Fitness centre',
    description: 'Title for an offer page highlight tag shown when a fitness centre is available.',
  },
  fitnessCentreDescription: {
    id: 'jxolnUruWiXc2-EufVJ6',
    defaultMessage: 'For those who crave a workout',
    description: 'Description for an offer page highlight tag shown when a fitness centre is available.',
  },
  spaWellnessTitle: {
    id: '4CDdKMf7fw9lr_q5shi4',
    defaultMessage: 'Spa & wellness facilities',
    description: 'Title for an offer page highlight tag shown when spa or wellness facilities are available.',
  },
  spaWellnessDescription: {
    id: 'ZH3ju9yRbHeTeUA1Wx1B',
    defaultMessage: 'Relax and recharge onsite',
    description: 'Description for an offer page highlight tag shown when spa or wellness facilities are available.',
  },
  accessibleFacilitiesTitle: {
    id: 'X-AvldWyU6EvvQGkNKxD',
    defaultMessage: 'Accessible facilities',
    description: 'Title for an offer page highlight tag shown when accessible facilities are available.',
  },
  accessibleFacilitiesDescription: {
    id: 'Wr-4tFrEySQqbWEmJzsh',
    defaultMessage: 'Built with accessibility in mind',
    description: 'Description for an offer page highlight tag shown when accessible facilities are available.',
  },
})

const FAMILY_TAG_CONFIGS: Array<OfferPageHighlightTagConfig> = [
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.FAMILY_POOLS,
    icon: 'water',
    titleMessage: tagMessages.familyPoolsTitle,
    descriptionMessage: tagMessages.familyPoolsDescription,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.FAMILY_LOCATION,
    icon: 'trees',
    titleMessage: tagMessages.familyLocationTitle,
    descriptionMessage: tagMessages.familyLocationDescription,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.FAMILY_DINING,
    icon: 'restaurant',
    titleMessage: tagMessages.familyDiningTitle,
    descriptionMessage: tagMessages.familyDiningDescription,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.FAMILY_SPACE,
    icon: 'bed-sofa',
    titleMessage: tagMessages.familySpaceTitle,
    descriptionMessage: tagMessages.familySpaceDescription,
  },
]

const COUPLE_TAG_CONFIGS: Array<OfferPageHighlightTagConfig> = [
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.COUPLE_SUNSET,
    icon: 'sunset',
    titleMessage: tagMessages.coupleSunsetTitle,
    descriptionMessage: tagMessages.coupleSunsetDescription,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.COUPLE_COCKTAILS,
    icon: 'glass-martini',
    titleMessage: tagMessages.coupleCocktailsTitle,
    descriptionMessage: tagMessages.coupleCocktailsDescription,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.COUPLE_RESORT,
    icon: 'trees',
    titleMessage: tagMessages.coupleResortTitle,
    descriptionMessage: tagMessages.coupleResortDescription,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.COUPLE_SERVICE,
    icon: 'star',
    titleMessage: tagMessages.coupleServiceTitle,
    descriptionMessage: tagMessages.coupleServiceDescription,
  },
]

const DEFAULT_TAG_CONFIGS: Array<OfferPageHighlightTagConfig> = [
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.DEFAULT_RATING,
    icon: 'star',
    titleMessage: tagMessages.defaultRatingTitle,
    descriptionMessage: tagMessages.defaultRatingDescription,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.DEFAULT_LOCATION,
    icon: 'map-marker',
    titleMessage: tagMessages.defaultLocationTitle,
    descriptionMessage: tagMessages.defaultLocationDescription,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.DEFAULT_PERKS,
    icon: 'utensils',
    titleMessage: tagMessages.defaultPerksTitle,
    descriptionMessage: tagMessages.defaultPerksDescription,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.DEFAULT_SETTING,
    icon: 'trees',
    titleMessage: tagMessages.defaultSettingTitle,
    descriptionMessage: tagMessages.defaultSettingDescription,
  },
]

const DATE_AWARE_DEFAULT_TAG_CONFIGS: Array<OfferPageHighlightTagConfig> = [
  DEFAULT_TAG_CONFIGS[0],
  {
    ...DEFAULT_TAG_CONFIGS[1],
    titleMessage: tagMessages.defaultLocationWithDatesTitle,
  },
  DEFAULT_TAG_CONFIGS[2],
  DEFAULT_TAG_CONFIGS[3],
]

const COUPLES_HOLIDAY_TYPE_SLUGS = new Set([
  'honeymoon',
  'romantic',
])

// Offer `tags.holidayTypes` value from API (exact string).
const ALL_INCLUSIVE_HOLIDAY_TYPE = 'All-inclusive'
const ALL_INCLUSIVE_HOLIDAY_TYPE_SLUG = slugify(ALL_INCLUSIVE_HOLIDAY_TYPE)

function hasRomanticHolidayTypeSignal(context: OfferPageHighlightTagContext | undefined) {
  return context?.holidayTypes?.some(holidayType => COUPLES_HOLIDAY_TYPE_SLUGS.has(slugify(holidayType))) ?? false
}

/**
 * Couple occupancy alone is not enough for romantic escape copy/tags.
 * Require offer holiday type Romantic or Honeymoon; otherwise fall back to default.
 */
function getHighlightCopyTravellerContext(
  context: OfferPageHighlightTagContext | undefined,
  travellerContext: OfferPageHighlightTravellerContext,
): OfferPageHighlightTravellerContext {
  if (travellerContext === 'couple' && !hasRomanticHolidayTypeSignal(context)) {
    return 'default'
  }

  return travellerContext
}

const MAPPED_TAG_LIMIT = 4
// Mapping doc requires 20 combined restaurant, cafe, and attraction POIs within 1.5km.
// The surroundings request currently fetches 10 places per category, so this is intentionally strict.
const GREAT_LOCATION_POI_CATEGORIES: Array<App.OfferPropertySurroundingsCategory> = [
  'restaurants_cafes',
  'top_attractions',
]
const GREAT_LOCATION_MAXIMUM_DISTANCE_M = 1500
const GREAT_LOCATION_MINIMUM_POI_COUNT = 20
const SURROUNDINGS_BACKED_TAG_IDS = new Set<OfferPageHighlightTagId>([
  OFFER_PAGE_HIGHLIGHT_TAG_IDS.BEACH_NEARBY,
  OFFER_PAGE_HIGHLIGHT_TAG_IDS.CENTRAL_LOCATION,
  OFFER_PAGE_HIGHLIGHT_TAG_IDS.GREAT_LOCATION,
  OFFER_PAGE_HIGHLIGHT_TAG_IDS.WALKABLE_NEIGHBOURHOOD,
  OFFER_PAGE_HIGHLIGHT_TAG_IDS.ARTS_CULTURE_NEARBY,
  OFFER_PAGE_HIGHLIGHT_TAG_IDS.GOLF_NEARBY,
  OFFER_PAGE_HIGHLIGHT_TAG_IDS.CLOSE_TO_TRANSPORT,
])
const ADULTS_ONLY_AREA_TERMS = [
  'adults only',
  'adults-only',
  'adult only',
]
const ADULTS_ONLY_AREA_LOCATION_TERMS = [
  'area',
  'pool',
  'section',
  'space',
  'zone',
]
const ADULTS_ONLY_NEGATIVE_TERMS = [
  'not adults only',
  'not adults-only',
  'not adult only',
  'not an adults only',
  'not an adults-only',
  'adults only unavailable',
  'adults-only unavailable',
  'adults only area unavailable',
  'adults-only area unavailable',
  'adults only pool unavailable',
  'adults-only pool unavailable',
]

// CRO6684 rule notes are kept with OFFER_PAGE_HIGHLIGHT_TAG_IDS.
const MAPPED_TAG_CONFIGS: Array<OfferPageHighlightMappingConfig> = [
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.MALDIVES_CHARTER,
    icon: 'plane',
    titleMessage: tagMessages.maldivesCharterTitle,
    descriptionMessage: tagMessages.maldivesCharterDescription,
    isEligible: ({ context }) => !!context?.charterBundleRoute,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.ALL_INCLUSIVE,
    icon: 'utensils',
    titleMessage: tagMessages.allInclusiveTitle,
    descriptionMessage: tagMessages.allInclusiveDescription,
    isEligible: ({ hasAllInclusive }) => hasAllInclusive,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.BREAKFAST_INCLUDED,
    icon: 'cloch',
    titleMessage: tagMessages.breakfastIncludedTitle,
    descriptionMessage: tagMessages.breakfastIncludedDescription,
    isEligible: ({ hasAllInclusive, hasBreakfastIncluded }) => !hasAllInclusive && hasBreakfastIncluded,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.ADULTS_ONLY,
    icon: 'sunset',
    titleMessage: tagMessages.adultsOnlyTitle,
    descriptionMessage: tagMessages.adultsOnlyDescription,
    isEligible: ({ isStructuredAdultsOnlyProperty }) => isStructuredAdultsOnlyProperty,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.ADULTS_ONLY_AREA,
    icon: 'sunset',
    titleMessage: tagMessages.adultsOnlyAreaTitle,
    descriptionMessage: tagMessages.adultsOnlyAreaDescription,
    isEligible: ({ hasAdultsOnlyText, isStructuredAdultsOnlyProperty }) => hasAdultsOnlyText && !isStructuredAdultsOnlyProperty,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.MASSAGE_INCLUDED,
    icon: 'wellness',
    titleMessage: tagMessages.massageIncludedTitle,
    descriptionMessage: tagMessages.massageIncludedDescription,
    isEligible: ({ hasMassageIncluded }) => hasMassageIncluded,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.KIDS_STAY_FREE,
    icon: 'kid',
    titleMessage: tagMessages.kidsStayFreeTitle,
    descriptionMessage: tagMessages.kidsStayFreeDescription,
    isEligible: ({ context, isAdultsOnlyProperty }) => !isAdultsOnlyProperty && hasInclusionCategory(context, ['One Child Stays Free', 'Multiple Children Stay Free']),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.DINING_CREDIT,
    icon: 'utensils',
    titleMessage: tagMessages.diningCreditTitle,
    descriptionMessage: tagMessages.diningCreditDescription,
    isEligible: ({ context }) => hasInclusionCategory(context, ['Dining Credit', 'Daily Dining Credit']),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.DAILY_COCKTAILS,
    icon: 'glass-martini',
    titleMessage: tagMessages.dailyCocktailsTitle,
    descriptionMessage: tagMessages.dailyCocktailsDescription,
    isEligible: ({ hasDailyCocktails }) => hasDailyCocktails,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.NIGHTLY_DRINK,
    icon: 'glass-martini',
    titleMessage: tagMessages.nightlyDrinkTitle,
    descriptionMessage: tagMessages.nightlyDrinkDescription,
    isEligible: ({ hasDailyCocktails, hasNightlyDrink }) => !hasDailyCocktails && hasNightlyDrink,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.LATE_CHECKOUT,
    icon: 'clock',
    titleMessage: tagMessages.lateCheckoutTitle,
    descriptionMessage: tagMessages.lateCheckoutDescription,
    isEligible: ({ context }) => hasInclusionCategory(context, ['Late Checkout / Early Check-In', 'Guaranteed Late Checkout']),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.GREAT_FOR_COUPLES,
    icon: 'heart-card',
    titleMessage: tagMessages.greatForCouplesTitle,
    descriptionMessage: tagMessages.greatForCouplesDescription,
    isEligible: ({ hasRomanticHolidayType, isAdultsOnlyProperty, isCouple }) => isCouple && hasRomanticHolidayType && !isAdultsOnlyProperty,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.FAMILY_FRIENDLY,
    icon: 'family',
    titleMessage: tagMessages.familyFriendlyTitle,
    descriptionMessage: tagMessages.familyFriendlyDescription,
    isEligible: ({ hasChild, hasFamilyFriendlySignals, isAdultsOnlyProperty }) => hasChild && hasFamilyFriendlySignals && !isAdultsOnlyProperty,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.HIGHLY_RATED,
    icon: 'star',
    titleMessage: tagMessages.highlyRatedTitle,
    descriptionMessage: tagMessages.highlyRatedDescription,
    isEligible: ({ context }) => (context?.rating?.reviewsTotal ?? 0) >= 5 && (context?.rating?.score ?? 0) >= 8.8,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.SKI_IN_SKI_OUT,
    icon: 'ski-lift',
    titleMessage: tagMessages.skiInSkiOutTitle,
    descriptionMessage: tagMessages.skiInSkiOutDescription,
    isEligible: () => false,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.BEACH_NEARBY,
    icon: 'water',
    titleMessage: tagMessages.beachNearbyTitle,
    descriptionMessage: tagMessages.beachNearbyDescription,
    isEligible: ({ context }) => hasSurroundingsPlaceWithinDistance(context, ['beach'], 1000),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.ATLAS_BEACH_CLUB,
    icon: 'water',
    titleMessage: tagMessages.atlasBeachClubTitle,
    descriptionMessage: tagMessages.atlasBeachClubDescription,
    isEligible: ({ hasAtlasBeachClub }) => hasAtlasBeachClub,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.CANNA_BALI_BEACH_CLUB_ACCESS,
    icon: 'water',
    titleMessage: tagMessages.cannaBaliBeachClubAccessTitle,
    descriptionMessage: tagMessages.cannaBaliBeachClubAccessDescription,
    isEligible: ({ context, hasAtlasBeachClub }) => !hasAtlasBeachClub && hasInclusionText(context, ['canna bali beach club', 'canna beach club']),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.CENTRAL_LOCATION,
    icon: 'map-marker',
    titleMessage: tagMessages.centralLocationTitle,
    descriptionMessage: tagMessages.centralLocationDescription,
    isEligible: ({ context }) => countSurroundingsPlacesWithinDistance(context, ['nearby'], 3000) >= 10,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.GREAT_LOCATION,
    icon: 'map-marker',
    titleMessage: tagMessages.greatLocationTitle,
    descriptionMessage: tagMessages.greatLocationDescription,
    isEligible: ({ context }) => countSurroundingsPlacesWithinDistance(context, GREAT_LOCATION_POI_CATEGORIES, GREAT_LOCATION_MAXIMUM_DISTANCE_M) >= GREAT_LOCATION_MINIMUM_POI_COUNT,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.WALKABLE_NEIGHBOURHOOD,
    icon: 'walking',
    titleMessage: tagMessages.walkableNeighbourhoodTitle,
    descriptionMessage: tagMessages.walkableNeighbourhoodDescription,
    isEligible: ({ context }) => countSurroundingsPlacesWithinDistance(context, ['restaurants_cafes', 'top_attractions'], 500) >= 10,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.ARTS_CULTURE_NEARBY,
    icon: 'theatre',
    titleMessage: tagMessages.artsCultureNearbyTitle,
    descriptionMessage: tagMessages.artsCultureNearbyDescription,
    isEligible: ({ context }) => hasSurroundingsPlaceMatchingTextWithinDistance(context, ['nearby', 'top_attractions'], ARTS_CULTURE_POI_TERMS, 2000),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.RETURN_AIRPORT_TRANSFERS,
    icon: 'plane',
    titleMessage: tagMessages.returnAirportTransfersTitle,
    descriptionMessage: tagMessages.returnAirportTransfersDescription,
    isEligible: ({ hasReturnAirportTransfers }) => hasReturnAirportTransfers,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.GOLF_NEARBY,
    icon: 'golf',
    titleMessage: tagMessages.golfNearbyTitle,
    descriptionMessage: tagMessages.golfNearbyDescription,
    isEligible: ({ context }) => hasSurroundingsPlaceMatchingTextWithinDistance(context, ['nearby', 'top_attractions'], GOLF_POI_TERMS, 10000),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.EASY_AIRPORT_ACCESS,
    icon: 'plane',
    titleMessage: tagMessages.easyAirportAccessTitle,
    descriptionMessage: tagMessages.easyAirportAccessDescription,
    isEligible: () => false,
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.CLOSE_TO_TRANSPORT,
    icon: 'car',
    titleMessage: tagMessages.closeToTransportTitle,
    descriptionMessage: tagMessages.closeToTransportDescription,
    isEligible: ({ context }) => hasSurroundingsPlaceMatchingTextWithinDistance(context, ['nearby'], TRANSPORT_POI_TERMS, 800),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.OCEAN_VIEW_ROOMS,
    icon: 'water',
    titleMessage: tagMessages.oceanViewRoomsTitle,
    descriptionMessage: tagMessages.oceanViewRoomsDescription,
    isEligible: ({ context }) => hasAnyText(context?.roomViewTags ?? [], ['ocean view', 'sea view', 'water view']),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.SPACIOUS_ROOMS,
    icon: 'bed-sofa',
    titleMessage: tagMessages.spaciousRoomsTitle,
    descriptionMessage: tagMessages.spaciousRoomsDescription,
    isEligible: ({ context }) => hasMinimumRoomSizeSqm(context, 35),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.INTERCONNECTING_ROOMS,
    icon: 'bed-sofa',
    titleMessage: tagMessages.interconnectingRoomsTitle,
    descriptionMessage: tagMessages.interconnectingRoomsDescription,
    isEligible: ({ hasTotalGuestCountForGroup, isGroupOrFamily, searchableText }) =>
      isGroupOrFamily && hasTotalGuestCountForGroup && hasAnyText(searchableText, ['interconnecting', 'connecting room', 'adjoining room']),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.FREE_WIFI,
    icon: 'wifi',
    titleMessage: tagMessages.freeWifiTitle,
    descriptionMessage: tagMessages.freeWifiDescription,
    isEligible: ({ searchableText }) => hasAnyText(searchableText, ['free wifi', 'free wi-fi', 'complimentary wifi', 'complimentary wi-fi']),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.RECEPTION,
    icon: 'clock',
    titleMessage: tagMessages.receptionTitle,
    descriptionMessage: tagMessages.receptionDescription,
    isEligible: ({ searchableText }) => hasAnyText(searchableText, ['24-hour front desk', '24 hour front desk', '24/7 front desk', '24-hour reception']),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.EXPRESS_CHECK_IN,
    icon: 'clock',
    titleMessage: tagMessages.expressCheckInTitle,
    descriptionMessage: tagMessages.expressCheckInDescription,
    isEligible: ({ searchableText }) => hasAnyText(searchableText, ['express check-in', 'express check in']),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.PARKING,
    icon: 'parking',
    titleMessage: tagMessages.parkingTitle,
    descriptionMessage: tagMessages.parkingDescription,
    isEligible: ({ context }) => hasInclusionCategory(context, ['Parking']),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.AIRPORT_TRANSFERS,
    icon: 'plane',
    titleMessage: tagMessages.airportTransfersTitle,
    descriptionMessage: tagMessages.airportTransfersDescription,
    isEligible: ({ context, hasReturnAirportTransfers }) => !hasReturnAirportTransfers && hasAnyText(context?.facilityNames ?? [], ['airport shuttle', 'airport transfer']),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.ONSITE_DINING,
    icon: 'restaurant',
    titleMessage: tagMessages.onsiteDiningTitle,
    descriptionMessage: tagMessages.onsiteDiningDescription,
    isEligible: ({ context }) => hasAnyText(context?.facilityNames ?? [], ['restaurant', 'onsite restaurant', 'on-site restaurant', 'dining']),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.BAR_LOUNGE,
    icon: 'glass-martini',
    titleMessage: tagMessages.barLoungeTitle,
    descriptionMessage: tagMessages.barLoungeDescription,
    isEligible: ({ context }) => hasAnyText(context?.facilityNames ?? [], ['bar', 'lounge']),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.ROOM_SERVICE,
    icon: 'cloch',
    titleMessage: tagMessages.roomServiceTitle,
    descriptionMessage: tagMessages.roomServiceDescription,
    isEligible: ({ searchableText }) => hasAnyText(searchableText, ['room service']),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.INDOOR_POOL,
    icon: 'swimmer',
    titleMessage: tagMessages.indoorPoolTitle,
    descriptionMessage: tagMessages.indoorPoolDescription,
    isEligible: ({ searchableText }) => hasAnyText(searchableText, ['indoor pool']),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.KIDS_POOL,
    icon: 'kid',
    titleMessage: tagMessages.kidsPoolTitle,
    descriptionMessage: tagMessages.kidsPoolDescription,
    isEligible: ({ isAdultsOnlyProperty, searchableText }) => !isAdultsOnlyProperty && hasAnyText(searchableText, ['kids pool', 'children pool', 'children\'s pool']),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.FITNESS_CENTRE,
    icon: 'dumbbell',
    titleMessage: tagMessages.fitnessCentreTitle,
    descriptionMessage: tagMessages.fitnessCentreDescription,
    isEligible: ({ searchableText }) => hasAnyText(searchableText, ['gym', 'fitness centre', 'fitness center', 'fitness facilities']),
  },
  {
    id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.SPA_WELLNESS,
    icon: 'wellness',
    titleMessage: tagMessages.spaWellnessTitle,
    descriptionMessage: tagMessages.spaWellnessDescription,
    isEligible: ({ hasMassageIncluded, searchableText }) => !hasMassageIncluded && hasAnyText(searchableText, ['spa', 'wellness centre', 'wellness center', 'sauna']),
  },
  // Disabled for now.
  // {
  //   id: OFFER_PAGE_HIGHLIGHT_TAG_IDS.ACCESSIBLE_FACILITIES,
  //   icon: 'wheelchair',
  //   titleMessage: tagMessages.accessibleFacilitiesTitle,
  //   descriptionMessage: tagMessages.accessibleFacilitiesDescription,
  //   isEligible: ({ context }) => hasAnyText(context?.facilityNames ?? [], ['accessible', 'wheelchair']),
  // },
]

const OFFER_PAGE_HIGHLIGHT_TAG_ICONS = new Set<OfferPageHighlightTagIcon>([
  'bed-sofa',
  'car',
  'cloch',
  'clock',
  'dumbbell',
  'family',
  'glass-martini',
  'golf',
  'grapes',
  'heart-card',
  'kid',
  'map-marker',
  'parking',
  'plane',
  'restaurant',
  'shop',
  'ski-lift',
  'snow-flake',
  'star',
  'sunset',
  'swimmer',
  'theatre',
  'trees',
  'utensils',
  'walking',
  'water',
  'wellness',
  'wheelchair',
  'wifi',
])

const OFFER_PAGE_HIGHLIGHT_TAG_ICON_BY_ID = new Map<OfferPageHighlightTagId, OfferPageHighlightTagIcon>([
  ...[
    ...MAPPED_TAG_CONFIGS,
    ...FAMILY_TAG_CONFIGS,
    ...COUPLE_TAG_CONFIGS,
    ...DEFAULT_TAG_CONFIGS,
  ].map(tagConfig => [tagConfig.id, tagConfig.icon] as const),
  [OFFER_PAGE_HIGHLIGHT_TAG_IDS.ACCESSIBLE_FACILITIES, 'wheelchair'],
])

function hasMinimumRoomSizeSqm(context: OfferPageHighlightTagContext | undefined, minimumSizeSqm: number) {
  const roomSizesSqm = context?.roomSizesSqm
  if (!roomSizesSqm?.length) return false
  if (context?.roomSizeAvailableRoomCount !== roomSizesSqm.length) return false

  return Math.min(...roomSizesSqm) >= minimumSizeSqm
}

function normaliseHighlightText(value: string) {
  return value.toLowerCase()
}

function escapeHighlightTextPattern(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function hasHighlightTextMatch(text: string, needle: string) {
  const textPattern = normaliseHighlightText(text)
  const needlePattern = escapeHighlightTextPattern(normaliseHighlightText(needle))
  const matchPattern = new RegExp(`(^|[^a-z0-9])${needlePattern}([^a-z0-9]|$)`)

  return matchPattern.test(textPattern)
}

function hasAnyText(textItems: Array<string>, needles: Array<string>) {
  return textItems.some(text => needles.some(needle => hasHighlightTextMatch(text, needle)))
}

function hasPositiveAdultsOnlyAreaText(textItems: Array<string>) {
  return textItems.some(text => (
    hasAnyText([text], ADULTS_ONLY_AREA_TERMS) &&
    hasAnyText([text], ADULTS_ONLY_AREA_LOCATION_TERMS) &&
    !hasAnyText([text], ADULTS_ONLY_NEGATIVE_TERMS)
  ))
}

function countSurroundingsPlacesWithinDistance(
  context: OfferPageHighlightTagContext | undefined,
  categories: Array<App.OfferPropertySurroundingsCategory>,
  maximumDistanceM: number,
) {
  return (context?.surroundings?.groups ?? []).reduce((count, group) => {
    if (!categories.includes(group.category)) return count

    return count + group.places.filter(place => place.distanceM <= maximumDistanceM).length
  }, 0)
}

function hasSurroundingsPlaceWithinDistance(
  context: OfferPageHighlightTagContext | undefined,
  categories: Array<App.OfferPropertySurroundingsCategory>,
  maximumDistanceM: number,
) {
  return countSurroundingsPlacesWithinDistance(context, categories, maximumDistanceM) > 0
}

function getSurroundingsPlaceText(place: App.OfferPropertySurroundingsPlace) {
  return [
    place.name,
    place.primaryTypeDisplayName,
  ].filter((text): text is string => !!text)
}

function hasSurroundingsPlaceMatchingTextWithinDistance(
  context: OfferPageHighlightTagContext | undefined,
  categories: Array<App.OfferPropertySurroundingsCategory>,
  terms: Array<string>,
  maximumDistanceM: number,
) {
  return (context?.surroundings?.groups ?? []).some(group => (
    categories.includes(group.category) &&
    group.places.some(place => (
      place.distanceM <= maximumDistanceM &&
      hasAnyText(getSurroundingsPlaceText(place), terms)
    ))
  ))
}

// Parking tag matching is disabled until structured parking availability data
// is available. Text inclusions can be limited offers, e.g. "1 hour free parking".
// const FREE_PARKING_INCLUDED_TERMS = [
//   'free',
//   'complimentary',
// ]
//
// const PARKING_INCLUDED_TERMS = [
//   'parking',
//   'self-parking',
//   'self parking',
//   'valet',
// ]
//
// const INCLUDED_PARKING_TERMS = [
//   'included parking',
//   'parking included',
//   'parking is included',
// ]
//
// const PAID_PARKING_TERMS = [
//   'additional cost',
//   'at cost',
//   'charge',
//   'excluded',
//   'fee',
//   'not included',
//   'paid',
//   'surcharge',
// ]

function getOfferPageHighlightSearchableText(context?: OfferPageHighlightTagContext) {
  return [
    ...(context?.facilities ?? []),
    ...(context?.facilityNames ?? []),
    ...(context?.holidayTypes ?? []),
    ...(context?.inclusions ?? []),
    ...(context?.locationTags ?? []),
    ...(context?.roomAmenities ?? []),
    ...(context?.roomNames ?? []),
    ...(context?.roomViewTags ?? []),
  ].filter(Boolean)
}

function hasBoardCode(context: OfferPageHighlightTagContext | undefined, boardCodes: Array<App.RateBoardCode>) {
  return context?.boardCodes?.some(boardCode => boardCodes.includes(boardCode)) ?? false
}

function hasInclusionCategory(
  context: OfferPageHighlightTagContext | undefined,
  categories: Array<App.PackageInclusionCategory>,
) {
  return context?.inclusionCategories?.some(category => categories.includes(category)) ?? false
}

function hasInclusionText(context: OfferPageHighlightTagContext | undefined, terms: Array<string>) {
  return hasAnyText(context?.inclusions ?? [], terms)
}

function hasReturnAirportTransfersSignal(context: OfferPageHighlightTagContext | undefined) {
  return hasInclusionText(context, [
    'return airport transfer',
    'return airport transfers',
    'roundtrip airport transfer',
    'round-trip airport transfer',
    'round trip airport transfer',
    'two-way airport transfer',
    'two way airport transfer',
    'airport transfers both ways',
  ])
}

function hasAllInclusiveHolidayTypeSignal(context: OfferPageHighlightTagContext | undefined) {
  return context?.holidayTypes?.some(holidayType => (
    holidayType === ALL_INCLUSIVE_HOLIDAY_TYPE ||
    slugify(holidayType) === ALL_INCLUSIVE_HOLIDAY_TYPE_SLUG
  )) ?? false
}

function hasAllInclusiveSignal(context: OfferPageHighlightTagContext | undefined, searchableText: Array<string>) {
  // Offer-level curated holiday type wins over package/rate inference (which often
  // collapses to boardCode LE when bonus/LuxPlus inclusions are present).
  if (hasAllInclusiveHolidayTypeSignal(context)) return true
  if (context?.hasAllInclusiveOnAllRates !== undefined) return context.hasAllInclusiveOnAllRates
  if (hasInclusionCategory(context, ['All-Inclusive Dining'])) return true
  if (hasBoardCode(context, ['allinclusive'])) return true

  return hasAnyText(searchableText, ['all-inclusive', 'all inclusive']) || (
    hasAnyText(searchableText, ['all meals', 'full board']) &&
    hasAnyText(searchableText, ['unlimited drinks', 'drinks included'])
  )
}

function hasBreakfastIncludedSignal(context: OfferPageHighlightTagContext | undefined) {
  if (context?.hasBreakfastIncludedOnAllRates !== undefined) return context.hasBreakfastIncludedOnAllRates

  return hasAnyText(context?.inclusions ?? [], ['breakfast'])
}

// function hasFreeParkingIncludedSignal(context: OfferPageHighlightTagContext | undefined) {
//   return (context?.inclusions ?? []).some(inclusion => (
//     (
//       (
//         hasAnyText([inclusion], PARKING_INCLUDED_TERMS) &&
//         hasAnyText([inclusion], FREE_PARKING_INCLUDED_TERMS)
//       ) ||
//       hasAnyText([inclusion], INCLUDED_PARKING_TERMS)
//     ) &&
//     !hasAnyText([inclusion], PAID_PARKING_TERMS)
//   ))
// }

function hasMassageIncludedSignal(context: OfferPageHighlightTagContext | undefined) {
  if (context?.hasMassageIncludedOnAllRates !== undefined) return context.hasMassageIncludedOnAllRates

  return hasInclusionText(context, ['massage', 'massages'])
}

function hasNightlyDrinkSignal(context: OfferPageHighlightTagContext | undefined) {
  if (context?.hasNightlyDrinkOnAllRates !== undefined) return context.hasNightlyDrinkOnAllRates

  return context?.inclusions?.some(inclusion => NIGHTLY_DRINK_INCLUSION_REGEX.test(inclusion)) ?? false
}

function hasDailyCocktailSignal(context: OfferPageHighlightTagContext | undefined) {
  return hasInclusionCategory(context, ['Nightly Cocktail']) && hasInclusionText(context, ['cocktail', 'cocktails'])
}

function getOfferPageHighlightMappingOptions(
  context: OfferPageHighlightTagContext | undefined,
  travellerContext: OfferPageHighlightTravellerContext,
  rooms: Array<App.Occupants> | undefined,
): OfferPageHighlightMappingOptions {
  const searchableText = getOfferPageHighlightSearchableText(context)
  const hasAdultsOnlyText = hasPositiveAdultsOnlyAreaText(searchableText)
  const hasAtlasBeachClub = hasInclusionText(context, ['atlas beach club'])
  const hasDailyCocktails = hasDailyCocktailSignal(context)
  const hasMassageIncluded = hasMassageIncludedSignal(context)
  const hasNightlyDrink = hasNightlyDrinkSignal(context)
  const hasReturnAirportTransfers = hasReturnAirportTransfersSignal(context)
  const totalGuests = rooms?.reduce(
    (sum, room) => sum + room.adults + (room.children ?? 0) + (room.infants ?? 0) + (room.teenagers ?? 0),
    0,
  ) ?? 0
  const isStructuredAdultsOnlyProperty = !!context?.isAdultsOnly
  const isAdultsOnlyProperty = isStructuredAdultsOnlyProperty

  return {
    context,
    hasAllInclusive: hasAllInclusiveSignal(context, searchableText),
    hasAdultsOnlyText,
    hasAtlasBeachClub,
    hasBreakfastIncluded: hasBreakfastIncludedSignal(context),
    hasChild: travellerContext === 'family',
    hasDailyCocktails,
    hasFamilyFriendlySignals: hasAnyText(searchableText, ['family', 'kids', 'children', 'babysitting', 'baby', 'child']),
    hasMassageIncluded,
    hasNightlyDrink,
    hasRomanticHolidayType: hasRomanticHolidayTypeSignal(context),
    hasReturnAirportTransfers,
    hasTotalGuestCountForGroup: totalGuests >= 3,
    isAdultsOnlyProperty,
    isStructuredAdultsOnlyProperty,
    isCouple: travellerContext === 'couple',
    isGroupOrFamily: travellerContext === 'family' || travellerContext === 'group',
    searchableText,
  }
}

function getMappedOfferPageHighlightTagConfigs(
  context: OfferPageHighlightTagContext | undefined,
  travellerContext: OfferPageHighlightTravellerContext,
  rooms: Array<App.Occupants> | undefined,
) {
  const mappingOptions = getOfferPageHighlightMappingOptions(context, travellerContext, rooms)

  return MAPPED_TAG_CONFIGS.filter(tagConfig => tagConfig.isEligible(mappingOptions))
}

export function shouldFetchOfferPageHighlightSurroundings(
  context: OfferPageHighlightTagContext | undefined,
  travellerContext: OfferPageHighlightTravellerContext,
  rooms: Array<App.Occupants> | undefined,
) {
  const firstSurroundingsTagIndex = MAPPED_TAG_CONFIGS.findIndex(tagConfig => SURROUNDINGS_BACKED_TAG_IDS.has(tagConfig.id))
  const mappingOptions = getOfferPageHighlightMappingOptions(context, travellerContext, rooms)
  const baseMappedTagsBeforeSurroundings = MAPPED_TAG_CONFIGS
    .slice(0, firstSurroundingsTagIndex)
    .filter(tagConfig => tagConfig.isEligible(mappingOptions))

  return baseMappedTagsBeforeSurroundings.length < MAPPED_TAG_LIMIT
}

function getOfferPageHighlightTitle(
  intl: IntlShape,
  {
    duration,
    firstName,
    hasSelectedDates,
    shouldUseFirstNameForNeutralContext,
    travellerContext,
  }: GetTitleOptions,
) {
  if (hasSelectedDates && duration) {
    if (firstName) {
      return intl.formatMessage(
        titleMessages.selectedDatesPersonalised,
        {
          firstName,
          duration,
        },
      )
    }

    return intl.formatMessage(
      titleMessages.selectedDates,
      {
        duration,
      },
    )
  }

  switch (travellerContext) {
    case 'family':
      if (firstName) {
        return intl.formatMessage(
          titleMessages.familyPersonalised,
          {
            firstName,
          },
        )
      }

      return intl.formatMessage(titleMessages.family)

    case 'couple':
      if (firstName) {
        return intl.formatMessage(
          titleMessages.couplePersonalised,
          {
            firstName,
          },
        )
      }

      return intl.formatMessage(titleMessages.couple)

    case 'group':
      return intl.formatMessage(titleMessages.group)

    case 'default':
      if (firstName && shouldUseFirstNameForNeutralContext) {
        return intl.formatMessage(
          titleMessages.neutralPersonalised,
          {
            firstName,
          },
        )
      }

      return intl.formatMessage(titleMessages.neutral)
  }
}

function getOfferPageHighlightTagConfigs(
  context: OfferPageHighlightTagContext | undefined,
  travellerContext: OfferPageHighlightTravellerContext,
  hasSelectedDates: boolean,
  rooms: Array<App.Occupants> | undefined,
) {
  const copyTravellerContext = getHighlightCopyTravellerContext(context, travellerContext)

  if (context) {
    const mappedTags = getMappedOfferPageHighlightTagConfigs(context, travellerContext, rooms).slice(0, MAPPED_TAG_LIMIT)

    if (mappedTags.length > 0) {
      return mappedTags
    }
  }

  if (hasSelectedDates) return DATE_AWARE_DEFAULT_TAG_CONFIGS

  switch (copyTravellerContext) {
    case 'family':
      return FAMILY_TAG_CONFIGS

    case 'couple':
      return COUPLE_TAG_CONFIGS

    case 'group':
    case 'default':
      return DEFAULT_TAG_CONFIGS
  }
}

function formatOfferPageHighlightTags(
  intl: IntlShape,
  tagConfigs: Array<OfferPageHighlightTagConfig>,
): Array<OfferPageHighlightTag> {
  return tagConfigs.map(tagConfig => ({
    id: tagConfig.id,
    icon: tagConfig.icon,
    title: intl.formatMessage(tagConfig.titleMessage),
    description: intl.formatMessage(tagConfig.descriptionMessage),
  }))
}

function isOfferPageHighlightTagIcon(icon: string): icon is OfferPageHighlightTagIcon {
  return OFFER_PAGE_HIGHLIGHT_TAG_ICONS.has(icon as OfferPageHighlightTagIcon)
}

function getOfferPageHighlightTagsOverrideIcon(tag: App.OfferPageHighlightTagOverride): OfferPageHighlightTagIcon | undefined {
  if (isOfferPageHighlightTagIcon(tag.icon)) return tag.icon

  return OFFER_PAGE_HIGHLIGHT_TAG_ICON_BY_ID.get(tag.id as OfferPageHighlightTagId)
}

function formatOfferPageHighlightTagsOverride(
  tagsOverride: Array<App.OfferPageHighlightTagOverride>,
): Array<OfferPageHighlightTag> {
  return tagsOverride.flatMap(tag => {
    const icon = getOfferPageHighlightTagsOverrideIcon(tag)
    if (!icon) return []

    return [{
      id: tag.id as OfferPageHighlightTagId,
      icon,
      title: tag.title,
      description: tag.description,
    }]
  })
}

function useOfferPageHighlightTags(props: Props) {
  const {
    context,
    duration,
    hasSelectedDates,
    isOccupancyContextKnown,
    offerPageHighlightTagsOverride,
    propertyId,
    rooms,
  } = props
  const intl = useIntl()
  const account = useAppSelector(state => state.auth.account)
  const firstName = account.memberId ? account.givenName : undefined

  const travellerContext = getOfferPageHighlightTravellerContext(rooms, isOccupancyContextKnown)
  const copyTravellerContext = getHighlightCopyTravellerContext(context, travellerContext)
  const [shouldUseFirstNameForNeutralContext, setShouldUseFirstNameForNeutralContext] = useState(false)

  useEffect(() => {
    if (!firstName || hasSelectedDates || copyTravellerContext !== 'default') {
      setShouldUseFirstNameForNeutralContext(false)
      return
    }

    const viewedPropertyIds = getViewedOfferPageHighlightPropertyIds()
    const shouldUseFirstName = shouldUseFirstNameForNeutralOfferPageHighlight(viewedPropertyIds)
    const nextViewedPropertyIds = Array.from(new Set([...viewedPropertyIds, propertyId])).slice(-2)

    setViewedOfferPageHighlightPropertyIds(nextViewedPropertyIds)
    setShouldUseFirstNameForNeutralContext(shouldUseFirstName)
  }, [copyTravellerContext, firstName, hasSelectedDates, propertyId])

  const title = useMemo(() => getOfferPageHighlightTitle(
    intl,
    {
      duration,
      firstName,
      hasSelectedDates,
      shouldUseFirstNameForNeutralContext,
      travellerContext: copyTravellerContext,
    },
  ), [copyTravellerContext, duration, firstName, hasSelectedDates, intl, shouldUseFirstNameForNeutralContext])

  const tags = useMemo<Array<OfferPageHighlightTag>>(() => {
    if (offerPageHighlightTagsOverride?.length) {
      const overrideTags = formatOfferPageHighlightTagsOverride(offerPageHighlightTagsOverride)
      if (overrideTags.length) return overrideTags
    }

    return formatOfferPageHighlightTags(
      intl,
      getOfferPageHighlightTagConfigs(context, travellerContext, hasSelectedDates, rooms),
    )
  }, [context, hasSelectedDates, intl, offerPageHighlightTagsOverride, rooms, travellerContext])

  return {
    title,
    tags,
  }
}

export default useOfferPageHighlightTags
